import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatCents,
  RECON_BUCKETS,
  type ReconBucketTotals,
  type ReconVerdict,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { SectionCard } from '../ui/Detail';
import { Notice, StatusBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/Skeleton';
import { EmptyState } from '../ui/EmptyState';
import { ErrorState } from '../ui/QueryStates';
import { IconMoney } from '../icons';

/**
 * Money → Books (PRD v2 §4). Does the money we say we made tie to the money in the books?
 *
 * The honest part is what it does BEFORE QuickBooks is connected: every issued invoice
 * reads as `pending`, because nothing has been pushed to a realm yet. That is the true
 * state, and it is said out loud rather than dressed up as a problem or hidden behind an
 * empty screen.
 */

const BUCKET_LABELS: Record<string, string> = {
  matched: 'Matched',
  pending: 'Waiting to push',
  failed: 'Failed',
  drift: 'Out of step',
  manual: 'Entered by hand',
};

/** Only `matched` is unambiguously good; `pending` is normal, the rest want a look. */
const BUCKET_TONE: Record<string, 'success' | 'neutral' | 'danger' | 'warn'> = {
  matched: 'success',
  pending: 'neutral',
  failed: 'danger',
  drift: 'warn',
  manual: 'neutral',
};

export function BooksPanel() {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const latest = useQuery(trpc.reconciliation.latest.queryOptions());
  const run = useMutation(
    trpc.reconciliation.run.mutationOptions({
      onSuccess: () => void qc.invalidateQueries(),
    }),
  );

  const year = new Date().getFullYear();
  const runNow = () =>
    run.mutate({ from: `${year}-01-01`, to: `${year}-12-31`, scope: 'on_demand' });

  if (latest.isPending) {
    return (
      <SectionCard title="Books">
        <Skeleton className="h-24" />
      </SectionCard>
    );
  }
  if (latest.isError) {
    return (
      <ErrorState
        icon={IconMoney}
        what="your books"
        onRetry={() => void latest.refetch()}
      />
    );
  }

  const data = latest.data;
  const buckets = (data?.bucketTotals ?? null) as ReconBucketTotals | null;
  const verdict = data?.verdict as ReconVerdict | undefined;
  const detail = (data?.detail ?? {}) as {
    invoicedCents?: number;
    collectedCents?: number;
  };

  /**
   * A shop that has never connected QuickBooks is NOT a shop whose books don't tie. The
   * delta is real either way, but "It doesn't tie" in red says something went wrong, and
   * nothing has. Only money that FAILED to push or has DRIFTED is a problem; money merely
   * waiting is the normal state of an unconnected account.
   */
  const headline =
    verdict === 'tie'
      ? {
          title: 'It ties',
          detail: 'Everything Servwell has matches your books',
          badge: <StatusBadge tone="success">To the penny</StatusBadge>,
        }
      : buckets && buckets.failed.count === 0 && buckets.drift.count === 0
        ? {
            title: 'Nothing pushed yet',
            detail: 'Nothing has gone wrong — none of it has reached QuickBooks',
            badge: (
              <StatusBadge tone="neutral">
                {formatCents(data?.deltaCents ?? 0)} waiting
              </StatusBadge>
            ),
          }
        : {
            title: "It doesn't tie",
            detail: 'Some of this needs a look',
            badge: (
              <StatusBadge tone="danger">
                {formatCents(data?.deltaCents ?? 0)}
              </StatusBadge>
            ),
          };

  return (
    <div className="space-y-4">
      <SectionCard
        title="Checking against QuickBooks"
        right={
          <Button disabled={run.isPending} onClick={runNow}>
            {run.isPending ? 'Checking…' : 'Check now'}
          </Button>
        }
      >
        <p className="text-sm text-muted-foreground">
          This compares what Servwell says you invoiced and received this year against
          what reached your books.
        </p>
        {/* Said once, plainly. Without it, a screen of "waiting to push" reads as a
            failure rather than as "nobody has connected QuickBooks yet". */}
        <div className="mt-2">
          <Notice tone="info">
            QuickBooks isn't connected on this account yet, so everything issued shows as
            waiting to push. That's the real state, not an error.
          </Notice>
        </div>
        {run.isError ? (
          <div className="mt-2">
            <Notice tone="danger">{run.error.message}</Notice>
          </div>
        ) : null}
      </SectionCard>

      {!data || !buckets ? (
        <EmptyState
          icon={IconMoney}
          title="Nothing checked yet"
          description="Run the first check and this fills in — what matched, what's still waiting, and anything that drifted."
          action={
            <Button disabled={run.isPending} onClick={runNow}>
              {run.isPending ? 'Checking…' : 'Check now'}
            </Button>
          }
        />
      ) : (
        <>
          <SectionCard title={headline.title} right={headline.badge}>
            {/* Invoiced here keeps the tax, unlike a report's pre-tax Invoiced: the
                reconciliation compares each invoice's total with what QuickBooks holds.
                Received is every payment still `succeeded`, kept as it is for matching the
                books: a payment refunded even in part, or disputed, is not in it. That is not
                Collected (payments received less the refunds made), so it is never called
                that here. */}
            <p className="text-sm text-muted-foreground">
              {headline.detail} · {formatCents(detail.invoicedCents ?? 0)} invoiced, tax
              included · {formatCents(detail.collectedCents ?? 0)} received, leaving out
              any payment refunded (even in part) or disputed · last checked{' '}
              {new Date(data.generatedAt).toLocaleString()}
            </p>
          </SectionCard>

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            {RECON_BUCKETS.map((b) => (
              <div
                key={b}
                className="rounded-[var(--radius)] border border-border bg-surface p-3"
              >
                <StatusBadge tone={BUCKET_TONE[b] ?? 'neutral'}>
                  {BUCKET_LABELS[b] ?? b}
                </StatusBadge>
                <p className="mt-2 text-xl font-bold tabular-nums">{buckets[b].count}</p>
                <p className="text-sm text-muted-foreground tabular-nums">
                  {formatCents(buckets[b].amountCents)}
                </p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
